import React, { FC, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Avatar,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle,
  Payment as PaymentIcon,
  GetApp as ExportIcon,
  TrendingUp as PerformanceIcon,
  Warning as WarningIcon,
  Refresh as RetryIcon,
  History as HistoryIcon,
  AccountBalance as BankIcon
} from '@material-ui/icons';

// Types
interface PayoutManagementProps {}

interface AffiliatePayout {
  id: string;
  affiliate: {
    id: string;
    name: string;
    email: string;
    uniqueCode: string;
  };
  amount: number;
  status: 'PENDING' | 'IN_TRANSIT' | 'PAID' | 'FAILED';
  paymentMethod: string;
  transactionId?: string;
  processedAt?: string;
  adminApproved: boolean;
  createdAt: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#02172a',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  headerSubtitle: {
    color: '#b0b0b0',
    fontSize: '1.1rem',
    lineHeight: 1.5,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
    },
  },
  tabsContainer: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    marginBottom: theme.spacing(3),
  },
  tab: {
    color: '#b0b0b0',
    fontWeight: 500,
    '&.Mui-selected': {
      color: '#1976d2',
    },
  },
  tabIndicator: {
    backgroundColor: '#1976d2',
  },
  filtersContainer: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2a3a4a',
      color: '#fff',
      '& fieldset': {
        borderColor: '#2a3a4a',
      },
      '&:hover fieldset': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
      '& .MuiOutlinedInput-input': {
        color: '#fff !important',
        '&::placeholder': {
          color: '#b0b0b0 !important',
          opacity: 1,
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: '#b0b0b0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1976d2',
    },
  },
  selectField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2a3a4a',
      color: '#fff',
      '& fieldset': {
        borderColor: '#2a3a4a',
      },
      '&:hover fieldset': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
      '& .MuiSelect-select': {
        color: '#fff !important',
      },
      '& .MuiSelect-icon': {
        color: '#fff !important',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#b0b0b0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1976d2',
    },
  },
  filterButton: {
    backgroundColor: '#1976d2',
    color: '#fff !important',
    border: '1px solid #1976d2',
    '&:hover': {
      backgroundColor: '#1565c0',
      borderColor: '#1565c0',
    },
    '& .MuiButton-label': {
      color: '#fff !important',
    },
    '& .MuiSvgIcon-root': {
      color: '#fff !important',
    },
  },
  statsGrid: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
      borderColor: '#1976d2',
    },
  },
  statCardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  statNumber: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#b0b0b0',
    fontSize: '0.9rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statIcon: {
    width: 48,
    height: 48,
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    overflow: 'hidden',
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#02172a',
    },
    '& .MuiTableCell-head': {
      color: '#1976d2',
      fontWeight: 600,
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid #2a3a4a',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid #2a3a4a',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
  },
  pagination: {
    backgroundColor: '#1a2332',
    borderTop: '1px solid #2a3a4a',
    '& .MuiTablePagination-root': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-toolbar': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectLabel': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-displayedRows': {
      color: '#fff !important',
    },
    '& .MuiIconButton-root': {
      color: '#b0b0b0 !important',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
      },
    },
    '& .MuiSelect-root': {
      color: '#fff !important',
    },
    '& .MuiSelect-icon': {
      color: '#fff !important',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#2a3a4a',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#1976d2',
    },
  },
  chip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  exportButton: {
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: '1px solid #1976d2',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      borderColor: '#1565c0',
    },
  },
  processButton: {
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
}));

const PayoutManagement: FC<PayoutManagementProps> = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Mock data for now - will be replaced with GraphQL queries
  const payouts: AffiliatePayout[] = [];
  const loading = false;
  const error = null;

  // Computed values
  const filteredPayouts = payouts.filter(payout =>
    payout.affiliate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payout.affiliate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payout.affiliate.uniqueCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#ff9800';
      case 'IN_TRANSIT': return '#2196f3';
      case 'PAID': return '#4caf50';
      case 'FAILED': return '#f44336';
      default: return '#b0b0b0';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <WarningIcon />;
      case 'IN_TRANSIT': return <PaymentIcon />;
      case 'PAID': return <CheckCircle />;
      case 'FAILED': return <WarningIcon />;
      default: return <WarningIcon />;
    }
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <LinearProgress />
        <Typography variant="h6" style={{ marginTop: 16, color: '#fff' }}>
          Loading payouts...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">
          Error loading payouts. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.headerTitle}>
          Payout Management
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Manage affiliate payouts and track payment status
        </Typography>
        <Box className={classes.actionButtons} style={{ marginTop: 24 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            className={classes.exportButton}
            disabled
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            className={classes.processButton}
            disabled
          >
            Process Payouts
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper className={classes.tabsContainer}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          classes={{
            indicator: classes.tabIndicator,
          }}
        >
          <Tab 
            className={classes.tab}
            label={
              <Badge badgeContent={0} color="primary">
                All Payouts
              </Badge>
            } 
          />
          <Tab 
            className={classes.tab}
            label={
              <Badge badgeContent={0} color="secondary">
                Pending
              </Badge>
            } 
          />
          <Tab 
            className={classes.tab}
            label={
              <Badge badgeContent={0} color="primary">
                In Transit
              </Badge>
            } 
          />
          <Tab 
            className={classes.tab}
            label={
              <Badge badgeContent={0} color="secondary">
                Completed
              </Badge>
            } 
          />
          <Tab 
            className={classes.tab}
            label={
              <Badge badgeContent={0} color="error">
                Failed
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper className={classes.filtersContainer}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search affiliates..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={classes.searchField}
              style={{ color: '#fff' }}
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, color: '#b0b0b0' }} />,
                style: { color: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth className={classes.selectField}>
              <InputLabel style={{ color: '#b0b0b0' }}>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ color: '#fff' }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: '#1a2332',
                      color: '#fff',
                      border: '1px solid #2a3a4a',
                    },
                  },
                }}
              >
                <MenuItem value="ALL" style={{ color: '#fff' }}>All Statuses</MenuItem>
                <MenuItem value="PENDING" style={{ color: '#fff' }}>Pending</MenuItem>
                <MenuItem value="IN_TRANSIT" style={{ color: '#fff' }}>In Transit</MenuItem>
                <MenuItem value="PAID" style={{ color: '#fff' }}>Paid</MenuItem>
                <MenuItem value="FAILED" style={{ color: '#fff' }}>Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<FilterIcon style={{ color: '#fff' }} />}
              className={classes.filterButton}
              style={{ color: '#fff' }}
              disabled
            >
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#1976d2' }}
              >
                <PaymentIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                $0.00
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Payouts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#4caf50' }}
              >
                <CheckCircle />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                0
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#ff9800' }}
              >
                <WarningIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                0
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#f44336' }}
              >
                <WarningIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                0
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Failed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payouts Table */}
      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Affiliate</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                      No payouts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" style={{ color: '#fff' }}>
                          {payout.affiliate.name}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                          {payout.affiliate.email}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                          {payout.affiliate.uniqueCode}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" style={{ color: '#fff' }}>
                        ${payout.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payout.status}
                        size="small"
                        className={classes.chip}
                        icon={getStatusIcon(payout.status)}
                        style={{ 
                          backgroundColor: getStatusColor(payout.status),
                          color: '#fff'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ color: '#fff' }}>
                        {new Date(payout.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ 
                          color: '#1976d2', 
                          borderColor: '#1976d2',
                        }}
                        disabled
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={[5, 10, 25]}
          count={filteredPayouts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          SelectProps={{
            style: {
              color: '#fff',
            },
            MenuProps: {
              PaperProps: {
                style: {
                  backgroundColor: '#1a2332',
                  color: '#fff',
                  border: '1px solid #2a3a4a',
                },
              },
            },
          }}
        />
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PayoutManagement;